import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { MAIN_WEBSITE_URL } from '@/mocks/site';
import JsonLd from '@/components/feature/JsonLd';
import { buildArticleJsonLd } from '@/lib/seo';

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 mt-10">
      <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-foreground-950">
        {title}
      </h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

export default function Article() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toc = [
    { id: 'what', label: t('article.whatTitle') },
    { id: 'how', label: t('article.howTitle') },
    { id: 'links', label: t('article.linksTitle') },
    { id: 'seller', label: t('article.sellerTitle') },
    { id: 'product', label: t('article.productTitle') },
    { id: 'qc', label: t('article.qcTitle') },
    { id: 'shipping', label: t('article.shippingTitle') },
    { id: 'warehouse', label: t('article.warehouseTitle') },
    { id: 'safety', label: t('article.safetyTitle') },
    { id: 'review', label: t('article.reviewTitle') },
    { id: 'faq', label: t('article.faqTitle') },
  ];

  const faqItems = [
    { q: 'article.fq1', a: 'article.fa1' },
    { q: 'article.fq2', a: 'article.fa2' },
    { q: 'article.fq3', a: 'article.fa3' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleJsonLd()} />
      <Navbar />

      <main className="pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              {t('nav.home')}
            </Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            </span>
            <span className="text-foreground-700">{t('nav.guide')}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-book-open-line text-sm" aria-hidden="true"></i>
              </span>
              USFans Spreadsheet
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
              {t('article.h1')}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {t('article.meta')}
            </p>
            <p className="mt-3 text-xs text-foreground-400">{t('article.updated')} · 2026</p>
          </header>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <p className="text-sm font-semibold text-foreground-900 mb-3">{t('article.toc')}</p>
            <div className="flex flex-wrap gap-2">
              {toc.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="whitespace-nowrap rounded-full bg-background-50 border border-background-200 px-3 py-1 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.intro1')}</p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.intro2')}</p>
          </div>

          <Section id="what" title={t('article.whatTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.what1')}</p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.what2')}</p>
          </Section>

          <Section id="how" title={t('article.howTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.how1')}</p>
            <ul className="space-y-2 pl-1">
              {['article.li1', 'article.li2', 'article.li3', 'article.li4'].map((key) => (
                <li key={key} className="flex items-start gap-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5">
                    <i className="ri-check-line text-base" aria-hidden="true"></i>
                  </span>
                  {t(key)}
                </li>
              ))}
            </ul>
          </Section>

          <Section id="links" title={t('article.linksTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.links1')}</p>
          </Section>

          <Section id="seller" title={t('article.sellerTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.seller1')}</p>
          </Section>

          <Section id="product" title={t('article.productTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.product1')}</p>
          </Section>

          <Section id="qc" title={t('article.qcTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.qc1')}</p>
          </Section>

          <Section id="shipping" title={t('article.shippingTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.shipping1')}</p>
          </Section>

          <Section id="warehouse" title={t('article.warehouseTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.warehouse1')}</p>
          </Section>

          <Section id="safety" title={t('article.safetyTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.safety1')}</p>
          </Section>

          <Section id="review" title={t('article.reviewTitle')}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article.review1')}</p>
          </Section>

          <section id="faq" className="scroll-mt-24 mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-foreground-950">
              {t('article.faqTitle')}
            </h2>
            <div className="mt-5 space-y-3">
              {faqItems.map((item, index) => {
                const isOpen = openIndex === index;
                return (
                  <div key={item.q} className="rounded-lg border border-background-200 bg-background-100 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="text-sm md:text-base font-semibold text-foreground-900">{t(item.q)}</span>
                      <span className={`w-5 h-5 flex items-center justify-center text-foreground-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-lg" aria-hidden="true"></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{t(item.a)}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <div className="mt-12 rounded-lg bg-primary-600 p-6 md:p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-background-50">
              {t('article.ctaTitle')}
            </h2>
            <p className="mt-3 text-sm md:text-base text-primary-50 leading-relaxed">{t('article.ctaText')}</p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MAIN_WEBSITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 hover:bg-accent-600 transition-colors cursor-pointer"
              >
                {t('article.ctaBtn')}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
                </span>
              </a>
              <Link
                to="/"
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                {t('article.backHome')}
              </Link>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              {t('category.title')}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap rounded-full bg-background-100 border border-background-200 px-3 py-1.5 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {t(`category.${cat.key}`)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}